import PropTypes from 'prop-types';
import { BsEnvelope, BsTelephone } from 'react-icons/bs';

import Checkbox from '#globalComponents/Checkbox/Checkbox';
import useContactRoles from '#globalHooks/useContactRoles';
import { truncateName } from '#lib/textUtils';

function contactDisplayName(contact) {
	return (
		contact.fullName ||
		[contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
		contact.emailAddress ||
		'–'
	);
}

const ContactSubRow = ({ contact, colSpan, isSelected, onToggle }) => {
	const { roleLabel } = useContactRoles();
	const displayName = contactDisplayName(contact);

	return (
		<tr className="border-b border-bgBase-400 bg-bgBase-300/40">
			<td colSpan={colSpan} className="px-4 py-2">
				<div className="flex items-center gap-2.5 pl-8">
					<Checkbox
						id={`contact-${contact.id}`}
						checked={isSelected}
						onChange={() => onToggle(contact.id)}
						aria-label="Kontakt auswählen"
					/>
					<div className="w-7 h-7 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
						{displayName[0]?.toUpperCase() ?? '?'}
					</div>
					<div className="min-w-0 flex-1">
						<div className="font-semibold text-text truncate" title={displayName}>
							{truncateName(displayName)}
							{contact.isPrimary && (
								<span className="ml-1.5 text-xs font-semibold text-caution-700 bg-caution-200 rounded-full px-1 py-0.5 uppercase">
									Primary
								</span>
							)}
						</div>
						{contact.companyRole && (
							<div className="text-sm text-bgBase-800 truncate">{roleLabel(contact.companyRole)}</div>
						)}
					</div>
					<div className="flex items-center gap-3 flex-shrink-0 text-bgBase-800">
						{contact.emailAddress && (
							<span className="inline-flex items-center gap-1 truncate max-w-[200px]">
								<BsEnvelope className="w-2.5 h-2.5 flex-shrink-0" /> {contact.emailAddress}
							</span>
						)}
						{contact.phoneNumber && (
							<span className="inline-flex items-center gap-1">
								<BsTelephone className="w-2.5 h-2.5 flex-shrink-0" /> {contact.phoneNumber}
							</span>
						)}
					</div>
				</div>
			</td>
		</tr>
	);
};

ContactSubRow.propTypes = {
	contact: PropTypes.object.isRequired,
	colSpan: PropTypes.number.isRequired,
	isSelected: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
};

const SubRowNotice = ({ colSpan, className = '', children }) => (
	<tr className="border-b border-bgBase-400 bg-bgBase-300/40">
		<td colSpan={colSpan} className={`px-4 py-3 pl-10 text-sm text-bgBase-800 ${className}`}>
			{children}
		</td>
	</tr>
);

SubRowNotice.propTypes = {
	colSpan: PropTypes.number.isRequired,
	className: PropTypes.string,
	children: PropTypes.node,
};

/** Contacts of an expanded company; `contacts === null` means still loading. */
const ContactSubRows = ({ contacts, colSpan, isContactSelected, onToggleContact }) => {
	if (contacts === null) return <SubRowNotice colSpan={colSpan}>Kontakte werden geladen …</SubRowNotice>;

	if (contacts.length === 0) {
		return (
			<SubRowNotice colSpan={colSpan} className="italic">
				Keine Kontakte hinterlegt.
			</SubRowNotice>
		);
	}

	return contacts.map((contact) => (
		<ContactSubRow
			key={contact.id}
			contact={contact}
			colSpan={colSpan}
			isSelected={isContactSelected(contact.id)}
			onToggle={onToggleContact}
		/>
	));
};

ContactSubRows.propTypes = {
	contacts: PropTypes.array,
	colSpan: PropTypes.number.isRequired,
	isContactSelected: PropTypes.func.isRequired,
	onToggleContact: PropTypes.func.isRequired,
};

export default ContactSubRows;
